import type {
  StreamMessage,
  ProgressUpdate,
  ScientificSourceContent,
  MateriomFormulationContent,
} from "../types/api";
import type {
  Source,
  ScientificSource,
  MateriomFormulationSource,
} from "../types/frontend";
import { ContentTypeValues, StreamMessageTypeValues } from "../types/api";

/**
 * Converts API source content to frontend Source format.
 * @param content - The API source content (ScientificSourceContent or MateriomFormulationContent)
 * @returns The converted frontend Source
 */
export const convertApiSourceToFrontendSource = (
  content: ScientificSourceContent | MateriomFormulationContent
): Source => {
  if (content.type === ContentTypeValues.ScientificSource) {
    const scientificSource = content as ScientificSourceContent;
    return {
      type: ContentTypeValues.ScientificSource,
      id: scientificSource.id,
      title: scientificSource.title,
      abstract: scientificSource.abstract,
      url: scientificSource.url,
      year: scientificSource.year,
      venue: scientificSource.venue,
    } as ScientificSource;
  }

  if (content.type === ContentTypeValues.MateriomFormulation) {
    const commonsRecipe = content as MateriomFormulationContent;
    return {
      type: ContentTypeValues.MateriomFormulation,
      id: commonsRecipe.id,
      url: commonsRecipe.url,
      name: commonsRecipe.name,
      ingredients: commonsRecipe.ingredients,
      ingredient_concentrations: commonsRecipe.ingredient_concentrations,
      ingredient_units: commonsRecipe.ingredient_units,
      author_ids: commonsRecipe.author_ids,
      author_names: commonsRecipe.author_names,
      author_last_names: commonsRecipe.author_last_names,
      contributor_ids: commonsRecipe.contributor_ids,
      contributor_names: commonsRecipe.contributor_names,
      contributor_last_names: commonsRecipe.contributor_last_names,
      methods: commonsRecipe.methods,
      material_properties: commonsRecipe.material_properties,
    } as MateriomFormulationSource;
  }

  throw new Error(`Unknown source type: ${(content as any).type}`);
};

/**
 * Processes a single stream message received from the server.
 * @param parsedChunk The parsed JSON chunk of the stream message.
 * @param fullResponse The current full response string.
 * @param isFirstChunk Indicates if this is the first chunk of the response.
 * @param updateLastMessage Function to update the last message in the chat context.
 * @param setProgressUpdate Function to set the progress update in the chat context.
 * @returns An object containing the new response string and a flag indicating if it's still the first chunk.
 */
export const processStreamMessage = (
  parsedChunk: StreamMessage,
  previousContent: string,
  isFirstChunk: boolean,
  updateLastMessage: (
    content: string,
    options?: { isProgress?: boolean }
  ) => void,
  setProgressUpdate: (progressUpdate: ProgressUpdate | null) => void,
  addSources: (sources: Source[]) => void,
  prependSources: (sources: Source[]) => void,
  existingSources: Source[]
): { newContent: string; isFirstChunk: boolean } => {
  if (parsedChunk.type === StreamMessageTypeValues.Progress) {
    setProgressUpdate(parsedChunk.update as ProgressUpdate);
    return { newContent: previousContent, isFirstChunk };
  }

  if (parsedChunk.type === StreamMessageTypeValues.Content) {
    // Reset the progress update
    setProgressUpdate(null);

    // Initialize the new content and sources
    let newContent = previousContent;
    let newSources: Source[] = [];

    // Handle string update
    if (typeof parsedChunk.update === "string") {
      newContent = previousContent + parsedChunk.update;

      // Handle content array update
    } else if (Array.isArray(parsedChunk.update)) {
      // Convert API sources to frontend sources
      for (const content of parsedChunk.update) {
        const source = convertApiSourceToFrontendSource(content);
        newSources.push(source);
      }
    }

    // Add the new sources to the sources context
    if (newSources.length > 0) {
      addSources(newSources);
      prependSources(newSources);
    }

    updateLastMessage(newContent, { isProgress: !isFirstChunk });
    return { newContent, isFirstChunk: false };
  }

  return { newContent: previousContent, isFirstChunk };
};

/**
 * Processes the buffer received from the server, extracting and processing individual stream messages.
 * @param buffer The buffer containing the stream data.
 * @param fullResponse The current full response string.
 * @param isFirstChunk Indicates if this is the first chunk of the response.
 * @param updateLastMessage Function to update the last message in the chat context.
 * @param setProgressUpdate Function to set the progress update in the chat context.
 * @returns An object containing the new response string, the remaining buffer, and a flag indicating if it's still the first chunk.
 */
export const processBuffer = (
  buffer: string,
  previousContent: string,
  isFirstChunk: boolean,
  updateLastMessage: (
    content: string,
    options?: { isProgress?: boolean }
  ) => void,
  setProgressUpdate: (progressUpdate: ProgressUpdate | null) => void,
  addSources: (sources: Source[]) => void,
  prependSources: (sources: Source[]) => void,
  existingSources: Source[]
): { newContent: string; newBuffer: string; isFirstChunk: boolean } => {
  let currentContent = previousContent;
  let currentIsFirstChunk = isFirstChunk;
  let startIndex = 0;

  while (startIndex < buffer.length) {
    try {
      const endIndex = buffer.indexOf("\n", startIndex);
      if (endIndex === -1) break;

      const jsonStr = buffer.slice(startIndex, endIndex).trim();
      if (jsonStr) {
        const parsedChunk = JSON.parse(jsonStr) as StreamMessage;
        const result = processStreamMessage(
          parsedChunk,
          currentContent,
          currentIsFirstChunk,
          updateLastMessage,
          setProgressUpdate,
          addSources,
          prependSources,
          existingSources
        );
        currentContent = result.newContent;
        currentIsFirstChunk = result.isFirstChunk;
      }
      startIndex = endIndex + 1;
    } catch (e) {
      break;
    }
  }

  return {
    newContent: currentContent,
    newBuffer: buffer.slice(startIndex),
    isFirstChunk: currentIsFirstChunk,
  };
};
